import { isProductEnabled } from '../config/productFocus';

export type AdminNavSection = 'main' | 'platform' | 'commerce' | 'finance' | 'ops' | 'hubs';

export type AdminNavItem = {
  id: string;
  title: string;
  titleKey?: string;
  path: string;
  icon: string;
  section: AdminNavSection;
  keywords?: string[];
  parentId?: string;
  /** Required permission to see this nav item (Phase 1 RBAC). */
  permission?: string;
};

/** Single source of truth for sidebar, breadcrumbs, Cmd+K. */
export const ADMIN_NAV: AdminNavItem[] = [
  {
    id: 'dashboard',
    title: 'Dashboard',
    path: '/admin',
    icon: 'feather-home',
    section: 'main',
    keywords: ['home', 'overview'],
    permission: 'dashboard.view',
  },
  {
    id: 'users',
    title: 'Users',
    path: '/admin/users',
    icon: 'feather-users',
    section: 'main',
    keywords: ['accounts', 'people'],
    permission: 'users.view',
  },
  {
    id: 'kyc',
    title: 'Identity (Didit)',
    path: '/admin/kyc',
    icon: 'feather-shield',
    section: 'main',
    keywords: ['verify', 'identity', 'didit', 'kyc'],
    permission: 'identity.view',
  },
  {
    id: 'roles',
    title: 'Roles',
    path: '/admin/roles',
    icon: 'feather-lock',
    section: 'main',
    keywords: ['permissions', 'rbac'],
    permission: 'roles.view',
  },
  {
    id: 'content',
    title: 'Content',
    path: '/admin/content',
    icon: 'feather-video',
    section: 'main',
    keywords: ['videos', 'media'],
    permission: 'content.view',
  },
  {
    id: 'businesses',
    title: 'Businesses',
    path: '/admin/businesses',
    icon: 'feather-briefcase',
    section: 'hubs',
    keywords: ['merchants', 'providers', 'hub'],
    permission: 'businesses.view',
  },
  {
    id: 'marketplace',
    title: 'Marketplace',
    path: '/admin/marketplace',
    icon: 'feather-shopping-cart',
    section: 'hubs',
    keywords: ['catalog', 'commerce hub'],
    permission: 'marketplace.view',
  },
  {
    id: 'analytics',
    title: 'Business Analytics',
    path: '/admin/analytics',
    icon: 'feather-bar-chart-2',
    section: 'platform',
    keywords: ['charts', 'metrics'],
    permission: 'analytics.view',
  },
  {
    id: 'feature-flags',
    title: 'Feature Flags',
    path: '/admin/feature-flags',
    icon: 'feather-toggle-left',
    section: 'platform',
    permission: 'feature_flags.view',
  },
  {
    id: 'moderation',
    title: 'Moderation',
    path: '/admin/moderation',
    icon: 'feather-flag',
    section: 'platform',
    permission: 'moderation.view',
  },
  {
    id: 'live-center',
    title: 'Live Center',
    path: '/admin/live-center',
    icon: 'feather-video',
    section: 'platform',
    keywords: ['live', 'streams', 'broadcast'],
    permission: 'live.view',
  },
  {
    id: 'verification-queue',
    title: 'Verification Queue',
    path: '/admin/verification-queue',
    icon: 'feather-check-circle',
    section: 'platform',
    permission: 'verification.view',
  },
  {
    id: 'disputes',
    title: 'Disputes',
    path: '/admin/disputes',
    icon: 'feather-alert-triangle',
    section: 'platform',
    permission: 'disputes.view',
  },
  {
    id: 'cms',
    title: 'CMS',
    path: '/admin/cms',
    icon: 'feather-file-text',
    section: 'platform',
    permission: 'cms.view',
  },
  {
    id: 'categories',
    title: 'Categories',
    path: '/admin/categories',
    icon: 'feather-grid',
    section: 'platform',
    permission: 'categories.view',
  },
  {
    id: 'app-settings',
    title: 'App Settings',
    path: '/admin/app-settings',
    icon: 'feather-settings',
    section: 'platform',
    permission: 'settings.view',
  },
  {
    id: 'orders',
    title: 'Orders',
    path: '/admin/orders',
    icon: 'feather-shopping-bag',
    section: 'commerce',
    permission: 'orders.view',
  },
  {
    id: 'cj-products',
    title: 'CJ Products',
    path: '/admin/cj-products',
    icon: 'feather-box',
    section: 'commerce',
    permission: 'products.view',
  },
  {
    id: 'payouts',
    title: 'Payouts',
    path: '/admin/payouts',
    icon: 'feather-send',
    section: 'commerce',
    permission: 'finance.view',
  },
  {
    id: 'finance',
    title: 'Finance & Billing',
    path: '/admin/finance-dashboard',
    icon: 'feather-dollar-sign',
    section: 'finance',
    keywords: ['money', 'treasury'],
    permission: 'finance.view',
  },
  {
    id: 'payment-activity',
    title: 'Payment activity',
    path: '/admin/payment-activity',
    icon: 'feather-activity',
    section: 'finance',
    parentId: 'finance',
    permission: 'payments.view',
  },
  {
    id: 'finance-dashboard',
    title: 'Finance dashboard',
    path: '/admin/finance-dashboard',
    icon: 'feather-pie-chart',
    section: 'finance',
    parentId: 'finance',
    permission: 'finance.view',
  },
  {
    id: 'finance-ledger',
    title: 'Finance ledger',
    path: '/admin/finance-ledger',
    icon: 'feather-layers',
    section: 'finance',
    parentId: 'finance',
    permission: 'finance.view',
  },
  {
    id: 'finance-reconciliation',
    title: 'Reconciliation',
    path: '/admin/finance-reconciliation',
    icon: 'feather-refresh-ccw',
    section: 'finance',
    parentId: 'finance',
    permission: 'finance.view',
  },
  {
    id: 'finance-booking-payouts',
    title: 'Booking payout release',
    path: '/admin/finance-booking-payouts',
    icon: 'feather-send',
    section: 'finance',
    parentId: 'finance',
    permission: 'finance.approve',
  },
  {
    id: 'finance-charges',
    title: 'Charges',
    path: '/admin/finance-charges',
    icon: 'feather-percent',
    section: 'finance',
    parentId: 'finance',
    permission: 'finance.configure',
  },
  {
    id: 'plans-pricing',
    title: 'Plans & pricing',
    path: '/admin/plans-pricing',
    icon: 'feather-tag',
    section: 'finance',
    parentId: 'finance',
    keywords: ['subscription', 'lifetime', 'merchant', 'pricing catalog', 'price', '4999'],
    permission: 'subscriptions.view',
  },
  {
    id: 'subscriptions',
    title: 'Subscriptions',
    path: '/admin/subscriptions',
    icon: 'feather-credit-card',
    section: 'finance',
    parentId: 'finance',
    permission: 'subscriptions.view',
  },
  {
    id: 'billing-webhooks',
    title: 'Billing webhooks',
    path: '/admin/billing-webhooks',
    icon: 'feather-repeat',
    section: 'finance',
    parentId: 'finance',
    permission: 'finance.view',
  },
  {
    id: 'rental-agreements',
    title: 'Rental Agreements',
    path: '/admin/rental-agreements',
    icon: 'feather-file-text',
    section: 'finance',
    parentId: 'finance',
    keywords: ['documents', 'contracts', 'esign', 'pandadoc', 'templates'],
    permission: 'finance.view',
  },
  {
    id: 'ai-infrastructure',
    title: 'BizHub AI & tokens',
    path: '/admin/ai-infrastructure',
    icon: 'feather-cpu',
    section: 'finance',
    parentId: 'finance',
    permission: 'ai.view',
  },
  {
    id: 'rewards',
    title: 'Rewards',
    path: '/admin/rewards',
    icon: 'feather-gift',
    section: 'ops',
    permission: 'rewards.view',
  },
  {
    id: 'referrals',
    title: 'Referrals',
    path: '/admin/referrals',
    icon: 'feather-share-2',
    section: 'ops',
    keywords: ['invite', 'referral code', 'credited', 'signup bonus'],
    permission: 'referrals.view',
  },
  {
    id: 'creator-rewards',
    title: 'Creator Rewards',
    path: '/admin/creator-rewards',
    icon: 'feather-dollar-sign',
    section: 'ops',
    keywords: [
      'profile rewards',
      'ads reward',
      'referrals earned',
      'payout threshold',
      '2000',
      'monetization',
      'credited',
    ],
    permission: 'creator_rewards.view',
  },
  {
    id: 'calendar',
    title: 'Calendar Hub',
    path: '/admin/calendar',
    icon: 'feather-calendar',
    section: 'ops',
    keywords: ['calendar', 'sync', 'webhooks', 'connections', 'queues', 'google', 'scheduling'],
    permission: 'calendar.view',
  },
  {
    id: 'ads',
    title: 'Ads',
    path: '/admin/ads',
    icon: 'feather-aperture',
    section: 'ops',
    permission: 'ads.view',
  },
  {
    id: 'logs',
    title: 'Activity Logs',
    path: '/admin/logs',
    icon: 'feather-file-text',
    section: 'ops',
    keywords: ['audit', 'history'],
    permission: 'logs.view',
  },
];

export const SECTION_LABELS: Record<AdminNavSection, string> = {
  main: 'Overview',
  hubs: 'Hubs',
  platform: 'Platform',
  commerce: 'Commerce',
  finance: 'Finance',
  ops: 'Operations',
};

export function normalizeAdminPath(pathname: string): string {
  const p = pathname.replace(/\/$/, '') || '/';
  return p;
}

export function findNavItemByPath(pathname: string): AdminNavItem | undefined {
  const p = normalizeAdminPath(pathname);
  if (p === '/admin') return ADMIN_NAV.find((i) => i.path === '/admin');
  const exact = ADMIN_NAV.find((i) => i.path === p);
  if (exact) return exact;
  return ADMIN_NAV.filter((i) => i.path !== '/admin')
    .filter((i) => p.startsWith(i.path))
    .sort((a, b) => b.path.length - a.path.length)[0];
}

export function getBreadcrumbTrail(pathname: string): AdminNavItem[] {
  const item = findNavItemByPath(pathname);
  if (!item) return [];
  const trail: AdminNavItem[] = [item];
  if (item.parentId) {
    const parent = ADMIN_NAV.find((i) => i.id === item.parentId);
    if (parent) trail.unshift(parent);
  }
  return trail;
}

export function filterNavByPermission(
  items: AdminNavItem[],
  can: (permission: string) => boolean
): AdminNavItem[] {
  return items.filter((item) => {
    if (item.permission && !can(item.permission)) return false;
    if (
      (item.id === 'marketplace' || item.id === 'orders' || item.id === 'cj-products') &&
      !isProductEnabled('store')
    ) {
      return false;
    }
    return true;
  });
}

export function searchNav(query: string, can?: (permission: string) => boolean): AdminNavItem[] {
  const q = query.trim().toLowerCase();
  let items = ADMIN_NAV.filter((i) => i.id !== 'finance' || !i.parentId);
  if (can) items = filterNavByPermission(items, can);
  if (!q) return items;
  return items.filter((item) => {
    if (item.id === 'finance' && !item.parentId) return false;
    const hay = [item.title, item.path, ...(item.keywords || [])].join(' ').toLowerCase();
    return hay.includes(q);
  });
}

export function navBySection(section: AdminNavSection): AdminNavItem[] {
  return ADMIN_NAV.filter((i) => i.section === section && !i.parentId);
}

export function financeChildren(): AdminNavItem[] {
  return ADMIN_NAV.filter((i) => i.parentId === 'finance');
}
